import React, { FC, useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import useLocales from 'hooks/useLocales';
import { Form, Formik, FormikHelpers } from 'formik';
import FormField from 'components/FormField/Index';
import { InputType } from 'core/enum/inputType';
import {
  emailValues,
  emailValidation,
  passwordValidation,
  passwordValues,
  nameSurnameValues,
  nameSurnameValidation,
  jobTitleValues,
  jobTitleValidation
} from './form';
import {
  IChangeEmail,
  IChangeNameSurname,
  IChangePassword,
  IChangeJobTitle
} from 'core/interface/settings';
import Button from 'components/Button/Index';
import Image from 'components/image/Image';
import { AccountServce } from 'services/upload/file-upload';
import { IMe } from 'core/interface/me';
import { useSelector } from 'react-redux';
import { ISettingsPopup } from 'core/interface/profileSettings';
import { AuthService } from 'services/auth/auth';
import { JobService } from 'services/jobs/job';

import './Index.scss';

interface IProfileSettingsProps extends ISettingsPopup {
  openAvatarModal?: () => void;
  isForActivationStep?: boolean;
  setCompanyData?: (data: any) => void;
  companyData?: any;
}

const ProfileSettings: FC<IProfileSettingsProps> = ({
  handleClosePopup,
  openAvatarModal,
  isForActivationStep = false,
  setCompanyData,
  companyData
}): JSX.Element => {
  const { t } = useLocales();
  const me = useSelector((state: IMe) => state.user).user;

  const { enqueueSnackbar } = useSnackbar();

  const [loader, setLoader] = useState<boolean>(false);

  useEffect(() => {
    getCities();
  }, []);

  const getCities = (limit = 10, query = '') => {
    JobService.getLocations({
      limit,
      q: query
    });
  };

  const handleNameSurnameChange = (
    values: IChangeNameSurname,
    actions: FormikHelpers<IChangeNameSurname>
  ) => {
    setLoader(true);
    const formData = new FormData();
    formData.append('first_name', values.first_name);
    formData.append('last_name', values.last_name);

    AccountServce.updateAccount(me.id, formData)
      .then(() => {
        handleClosePopup();
      })
      .catch((error) => {
        console.log(error);
        enqueueSnackbar(t('update_failed'), { variant: 'error' });
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleEmailChange = (
    values: IChangeEmail,
    actions: FormikHelpers<IChangeEmail>
  ) => {
    setLoader(true);
    updateAccount(values.email);
  };

  const updateAccount = async (newEmail: string): Promise<void> => {
    setLoader(true);
    const body = {
      email: newEmail
    };
    const formData = new FormData();
    for (const key in body) {
      if (key !== 'id') {
        formData.append(key, (body as any)[key]);
      }
    }
    try {
      await AccountServce.updateAccount(me.id, formData);
      handleClosePopup();
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const handlePasswordChange = async (
    values: IChangePassword,
    actions: FormikHelpers<IChangePassword>
  ) => {
    setLoader(true);
    const body = {
      password: values.newPassword
    };
    const formData = new FormData();
    for (const key in body) {
      if (key !== 'id') {
        formData.append(key, (body as any)[key]);
      }
    }
    try {
      await AuthService.changePassword({
        old_password: values.oldPassword,
        new_password: values.newPassword
      });
      await AccountServce.updateAccount(me.id, formData);
      handleClosePopup();
    } catch (error) {
      console.log(error);
      enqueueSnackbar(t('update_failed'), { variant: 'error' });
    } finally {
      setLoader(false);
    }
  };

  const handleJobTitleChange = (
    values: IChangeJobTitle,
    actions: FormikHelpers<IChangeJobTitle>
  ) => {
    setLoader(true);
    const formData = new FormData();
    formData.append('job_title', values.job_title);

    AccountServce.updateAccount(me.id, formData)
      .then(() => {
        handleClosePopup();
      })
      .catch((error) => {
        console.log(error);
        enqueueSnackbar(t('update_failed'), { variant: 'error' });
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const formStyle = {
    marginRight: isForActivationStep ? 0 : '20px'
  };

  return (
    <div className="profileForm  scrollable ">
      {me?.company && !isForActivationStep && (
        <div className="imageContainer">
          <div className="company__top__left__image">
            <div className="editButton" onClick={openAvatarModal}>
              <Image
                className="edit-img"
                name="penWhite.png"
                width={15}
                height={15}
              />
            </div>
            <Image
              className="company__top__left-image"
              url={me?.avatar}
              name="no-images.svg"
              width={243}
              height={145}
              objectFit="contain"
            />
          </div>
        </div>
      )}
      <div className="profileForm__email">
        <span className="section-label">{t('Your name')}</span>
        <span className="profileForm__email-email">
          {me.first_name} {me.last_name}
        </span>
      </div>
      <div className="profileForm__eform">
        <Formik
          validationSchema={nameSurnameValidation(t)}
          initialValues={
            isForActivationStep
              ? companyData || nameSurnameValues
              : nameSurnameValues
          }
          enableReinitialize
          onSubmit={(values, actions) => {
            if (isForActivationStep) return;
            handleNameSurnameChange(values, actions);
          }}
        >
          {({ isValid, values, setFieldValue, errors, touched }) => {
            useEffect(() => {
              if (isForActivationStep && setCompanyData) {
                setCompanyData({ ...companyData, ...values });
              }
            }, [values]);
            return (
              <Form>
                <div className="mail" style={formStyle}>
                  <span className="label">{t('Your new name')}</span>
                  <FormField
                    inputClassName="mail__input"
                    fieldName="first_name"
                    type={InputType.text}
                    placeholder={t('First name')}
                    onChange={(e) => {
                      setFieldValue('first_name', e.target.value);
                    }}
                  />

                  <FormField
                    inputClassName="mail__input"
                    fieldName="last_name"
                    type={InputType.text}
                    placeholder={t('Last name')}
                    onChange={(e) => {
                      setFieldValue('last_name', e.target.value);
                    }}
                  />
                </div>
                {!isForActivationStep && (
                  <Button
                    isLoading={loader}
                    t={t}
                    className={`save ${!isValid ? 'invalid-btn' : ''}`}
                    type="submit"
                    title="Save"
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
      {(me?.company || isForActivationStep) && (
        <>
          <div className="profileForm__email">
            <span className="section-label">{t('Your job title')}</span>
            <span className="profileForm__email-email">{me.job_title}</span>
          </div>
          <div className="profileForm__eform">
            <Formik
              validationSchema={jobTitleValidation(t)}
              initialValues={
                isForActivationStep
                  ? companyData || jobTitleValues
                  : jobTitleValues
              }
              enableReinitialize
              onSubmit={(values, actions) => {
                if (isForActivationStep) return;
                handleJobTitleChange(values, actions);
              }}
            >
              {({ isValid, values, setFieldValue, errors, touched }) => {
                useEffect(() => {
                  if (isForActivationStep && setCompanyData) {
                    setCompanyData({ ...companyData, ...values });
                  }
                }, [values]);
                return (
                  <Form>
                    <div className="mail" style={formStyle}>
                      <span className="label">{t('Your new job title')}</span>
                      <FormField
                        inputClassName="mail__input"
                        fieldName="job_title"
                        type={InputType.text}
                        placeholder={t('Job title')}
                        onChange={(e) => {
                          setFieldValue('job_title', e.target.value);
                        }}
                      />
                    </div>
                    {!isForActivationStep && (
                      <Button
                        isLoading={loader}
                        t={t}
                        className={`save ${!isValid ? 'invalid-btn' : ''}`}
                        type="submit"
                        title="Save"
                      />
                    )}
                  </Form>
                );
              }}
            </Formik>
          </div>
        </>
      )}
      <div className="profileForm__email">
        <span className="section-label">{t('Your email adresss')}</span>
        <span className="profileForm__email-email">{me.email}</span>
      </div>
      <div className="profileForm__eform">
        <Formik
          validationSchema={emailValidation(t)}
          initialValues={
            isForActivationStep ? companyData || emailValues : emailValues
          }
          enableReinitialize
          onSubmit={(values, actions) => {
            if (isForActivationStep) return;
            handleEmailChange(values, actions);
          }}
        >
          {({ isValid, values, setFieldValue, errors, touched }) => {
            useEffect(() => {
              if (isForActivationStep && setCompanyData) {
                setCompanyData({ ...companyData, ...values });
              }
            }, [values]);
            return (
              <Form>
                <div className="mail" style={formStyle}>
                  <span className="label">{t('Your new email address')}</span>
                  <FormField
                    inputClassName="mail__input"
                    fieldName="email"
                    type={InputType.email}
                    placeholder={t('Email address')}
                    onChange={(e) => {
                      setFieldValue('email', e.target.value);
                    }}
                  />
                </div>
                {!isForActivationStep && (
                  <Button
                    isLoading={loader}
                    t={t}
                    className={`save ${!isValid ? 'invalid-btn' : ''}`}
                    type="submit"
                    title="Save"
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
      <div className="profileForm__pass">
        <span className="section-label">{t('Change password')}</span>

        <Formik
          validationSchema={passwordValidation(t)}
          initialValues={passwordValues}
          onSubmit={(values, actions) => {
            if (values.oldPassword) {
              handlePasswordChange(values, actions);
            }
          }}
        >
          {({ errors, touched, isValid, values }) => (
            <Form style={{ marginTop: isForActivationStep ? '20px' : '0px' }}>
              <div className="passForm">
                <span className="label">{t('Old password')}</span>
                <FormField
                  inputClassName="passForm__input"
                  fieldName="oldPassword"
                  type={InputType.password}
                  placeholder={t('old password')}
                />

                <div className="repeats">
                  <div className="repeats__field">
                    <span className="label">{t('New password')}</span>
                    <FormField
                      inputClassName="passForm__input"
                      fieldName="newPassword"
                      type={InputType.password}
                      placeholder={t('new password')}
                    />
                  </div>
                  <div className="repeats__field">
                    <span className="label">{t('Repeat password')}</span>
                    <FormField
                      inputClassName="passForm__input"
                      fieldName="repeatPassword"
                      type={InputType.password}
                      placeholder={t('repeat password')}
                    />
                  </div>
                </div>
                {!isForActivationStep && (
                  <Button
                    t={t}
                    className={`change_pass ${
                      !values.oldPassword || !isValid ? 'invalid-btn' : ''
                    }`}
                    type="submit"
                    title="Save"
                    isLoading={loader}
                  />
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ProfileSettings;
